import React, { useEffect, useState, useMemo } from 'react';
import './WZDocuments.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Button, Space, App, Dropdown, Input, DatePicker, Row, Col, Card, Modal, Tag, Tooltip, Segmented } from 'antd';
import type { MenuProps } from 'antd';
import { getWZErrorMessage, isBlockingWZError, WZ_ERROR_CODE_KEYS } from '../modules/warehouse/wzErrorMessages';
import { findBlockedAction, getBlockedActionMessage } from '../modules/warehouse/blockedActions';
import { useFeedback } from '../utils/feedback';
import { PlusOutlined, DeleteOutlined, FilePdfOutlined, EditOutlined, EyeOutlined, SearchOutlined, ClearOutlined, FileOutlined, FilterOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import dayjs from 'dayjs';
import { WZDocumentDto, normalizeWZDocuments } from '../dto/wz';
import {
  canDeleteWarehouseDocument,
  canManageWZDocuments,
} from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { getAuthFileUrl } from '../utils/fileUrl';
import { canShowWZEditAction } from '../utils/wzEditRules';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { compareMonthlyDocumentNumbers } from '../utils/documentNumber';
import {
  WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM,
  getCancelledDocumentKind,
  parseWarehouseDocumentListView,
  partitionWarehouseDocumentsByView,
  type WarehouseDocumentListView,
} from '../modules/warehouse/warehouseDocumentListView';

const { RangePicker } = DatePicker;

const WZDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['wz', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [wzDocuments, setWzDocuments] = useState<WZDocumentDto[]>([]);
  // "Aktywne" vs "Anulowane i storno" lives in the URL (?view=cancelled) like
  // other list filters, so links and reloads land on the same category.
  const listView = parseWarehouseDocumentListView(searchParams.get(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM));
  const handleListViewChange = (value: WarehouseDocumentListView) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'active') {
      next.delete(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM);
    } else {
      next.set(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM, value);
    }
    setSearchParams(next);
  };
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<WZDocumentDto | null>(null);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageWZDocuments(currentUser);
  const canDelete = canDeleteWarehouseDocument(currentUser);
  const canDownloadWZPdf = authService.canDownloadWZDocumentPdf();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState<{
    dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    search: string;
  }>({
    dateRange: null,
    search: ''
  });

  useEffect(() => {
    fetchWZDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWZDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/wz');
      setWzDocuments(normalizeWZDocuments(response.data, 'wz.list'));
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  usePageAutoRefresh(fetchWZDocuments);

  const handleDelete = async (id: string, wzNumber: string) => {
    try {
      await api.delete(`/wz/${id}`);
      feedback.success(t('wz:success.deleted', { wzNumber }));
      fetchWZDocuments();
    } catch (error: any) {
      const data = error.response?.data;
      // With `relatedDocuments` the shared mapper names the PZ batches the WZ
      // consumed; the status-only sentence remains for older responses.
      if (data?.code === 'WZ_DELETE_BLOCKED_STATUS' && !(Array.isArray(data.relatedDocuments) && data.relatedDocuments.length > 0)) {
        // Interpolates the blocking status, so it cannot live in the shared
        // static code map — and it is a hard block, so it waits to be read.
        const statusLabel = data.status
          ? t(`wz:status.${String(data.status).toLowerCase()}`)
          : '';
        feedback.error(
          t('wz:error.deleteBlockedStatus', { wzNumber, status: statusLabel }),
          { sticky: true },
        );
      } else {
        feedback.error(getWZErrorMessage(error, t, 'wz:error.deleteFailed', { wzNumber }), {
          sticky: isBlockingWZError(error),
        });
      }
    }
  };

  const handleDownloadPDF = async (id: string, wzNumber: string) => {
    setDownloading(id);
    try {
      const response = await api.get(`/wz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${wzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('wz:success.pdfDownloaded', { wzNumber }));
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.pdfGenerateFailed'));
    } finally {
      setDownloading(null);
    }
  };

  const handlePreviewDocument = (doc: WZDocumentDto) => {
    setPreviewDoc(doc);
    setPreviewVisible(true);
  };

  const documentsByView = useMemo(() => partitionWarehouseDocumentsByView(wzDocuments), [wzDocuments]);
  const cancelledCount = documentsByView.cancelled.length;

  // Filter documents based on current filters
  const filteredDocuments = useMemo(() => {
    let filtered = listView === 'cancelled' ? [...documentsByView.cancelled] : [...documentsByView.active];

    // Date range filter
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = filters.dateRange[0].startOf('day');
      const endDate = filters.dateRange[1].endOf('day');
      filtered = filtered.filter(doc => {
        const issueDate = dayjs(doc.issueDate);
        return issueDate.isAfter(startDate) && issueDate.isBefore(endDate);
      });
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.wzNumber.toLowerCase().includes(searchLower) ||
        doc.recipientName.toLowerCase().includes(searchLower) ||
        doc.recipientCompany?.toLowerCase().includes(searchLower) ||
        doc.orderNumber.toLowerCase().includes(searchLower) ||
        doc.displayOrderNumber.toLowerCase().includes(searchLower) ||
        doc.clientOrderNumber?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [documentsByView, listView, filters]);

  const clearFilters = () => {
    setFilters({
      dateRange: null,
      search: ''
    });
  };

  const activeFilterCount = [
    Boolean(filters.search.trim()),
    Boolean(filters.dateRange && filters.dateRange[0] && filters.dateRange[1]),
  ].filter(Boolean).length;

  const getRowMenuItems = (record: WZDocumentDto): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [
      { key: 'view', icon: <EyeOutlined />, label: t('common:open') },
      { key: 'preview', icon: <FileOutlined />, label: t('wz:actions.preview') },
    ];

    if (canEdit && canShowWZEditAction(record.status)) {
      items.push({ key: 'edit', icon: <EditOutlined />, label: t('common:edit') });
    }

    if (canDownloadWZPdf) {
      items.push({ key: 'pdf', icon: <FilePdfOutlined />, label: t('wz:actions.downloadPDF') });
    }

    if (canDelete) {
      // Backend-computed reason (list rows carry status-only ones): a released
      // WZ keeps its delete entry, disabled, with the explanation on hover.
      const blocked = findBlockedAction(record.blockedActions, 'delete');
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: blocked
          ? <Tooltip title={getBlockedActionMessage(blocked, t, 'wz', WZ_ERROR_CODE_KEYS)}><span>{t('common:delete')}</span></Tooltip>
          : t('common:delete'),
        danger: true,
        disabled: Boolean(blocked),
      });
    }

    return items;
  };

  const columns = [
    {
      title: t('wz:fields.wzNumber'),
      dataIndex: 'wzNumber',
      key: 'wzNumber',
      width: 140,
      sorter: (a: WZDocumentDto, b: WZDocumentDto, sortOrder?: 'ascend' | 'descend' | null) =>
        compareMonthlyDocumentNumbers(a.wzNumber, b.wzNumber, sortOrder),
      defaultSortOrder: 'descend' as const,
      render: (_: unknown, record: WZDocumentDto) => {
        const cancelledKind = getCancelledDocumentKind(record);
        return (
          <Space direction="vertical" size={0}>
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/app/wz/${record.id}`)}>
              {record.wzNumber || '-'}
            </Button>
            {cancelledKind && (
              <Tag color={cancelledKind === 'STORNO' ? 'warning' : 'error'} className="wz-documents-table__cancelled-kind">
                {t(`wz:cancelledKind.${cancelledKind.toLowerCase()}`)}
              </Tag>
            )}
            {record.pendingCostMarker?.costPending && <Tag color="warning" style={{ whiteSpace: 'normal' }}>{t('wz:pendingPurchaseCostShort')}</Tag>}
          </Space>
        );
      },
    },
    {
      title: t('wz:fields.issueDate'),
      dataIndex: 'issueDate',
      key: 'issueDate',
      width: 130,
      render: (date: string) => formatDate(date),
      sorter: (a: WZDocumentDto, b: WZDocumentDto) => dayjs(a.issueDate).unix() - dayjs(b.issueDate).unix(),
    },
    {
      title: t('wz:fields.orderNumber'),
      key: 'orderNumber',
      width: 160,
      render: (_: unknown, record: WZDocumentDto) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/app/orders/${record.orderId}`)}>
          {record.displayOrderNumber || record.orderNumber || '-'}
        </Button>
      ),
    },
    {
      title: t('wz:fields.recipient'),
      key: 'recipient',
      render: (_: unknown, record: WZDocumentDto) => (
        <div>
          <div>{record.recipientName}</div>
          {record.recipientCompany && (
            <div className="wz-documents-table__recipient-company">{record.recipientCompany}</div>
          )}
        </div>
      ),
    },
    {
      // I1 (coordinator triage, 2026-07-07): the WZ list previously showed no
      // transport link at all, even though it's exactly the binding that
      // BLOCKS transport deletion (WZ_TRANSPORT_LINK_EXISTS) — operators need
      // to see it where they browse first, not only after opening the detail
      // page. Same "route number as a link + carrier muted underneath" shape
      // used on the order detail's Transports tab (OrderTransportsSection),
      // and the same "+N with a title tooltip" truncation already used by the
      // items column just below for a >2-item list.
      title: t('wz:fields.linkedTransports'),
      key: 'linkedTransports',
      render: (_: unknown, record: WZDocumentDto) => {
        if (!record.linkedTransports || record.linkedTransports.length === 0) return '—';
        const [first, ...rest] = record.linkedTransports;
        return (
          <div>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => navigate(`/app/transports/${first.id}`)}
            >
              {first.routeNumber || '-'}
            </Button>
            {rest.length > 0 && (
              <span
                className="wz-documents-table__transport-company"
                title={record.linkedTransports.map((transport) => transport.routeNumber).join(', ')}
              >
                {' '}+{rest.length}
              </span>
            )}
            {first.transportCompany && (
              <div className="wz-documents-table__transport-company">{first.transportCompany}</div>
            )}
          </div>
        );
      },
    },
    {
      title: t('wz:fields.items'),
      key: 'items',
      render: (_: unknown, record: WZDocumentDto) => {
        if (!record.items || record.items.length === 0) return '-';
        const itemNames = record.items.map(item => item.productName);
        if (itemNames.length <= 2) {
          return itemNames.join(', ');
        }
        return (
          <span title={itemNames.join(', ')}>
            {itemNames.slice(0, 2).join(', ')} +{itemNames.length - 2}
          </span>
        );
      },
      ellipsis: true,
    },
    {
      title: t('common:actions'),
      key: 'actions',
      width: 80,
      align: 'right' as const,
      fixed: 'right' as const,
      render: (_: unknown, record: WZDocumentDto) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();

              if (key === 'view') {
                navigate(`/app/wz/${record.id}`);
              }

              if (key === 'preview') {
                handlePreviewDocument(record);
              }

              if (key === 'edit') {
                navigate(`/app/wz/${record.id}?mode=edit`);
              }

              if (key === 'pdf') {
                void handleDownloadPDF(record.id, record.wzNumber);
              }

              if (key === 'delete') {
                modal.confirm({
                  title: t('wz:confirm.delete'),
                  okText: t('common:yes'),
                  cancelText: t('common:no'),
                  okButtonProps: { danger: true },
                  onOk: () => { void handleDelete(record.id, record.wzNumber); },
                });
              }
            },
          }}
        >
          <Button
            className="wz-documents-row-menu-button"
            type="text"
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
            aria-label={t('common:actions')}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="wz-documents-page">
      <div className="wz-documents-page__toolbar">
        <div className="wz-documents-page__toolbar-main">
          <div className="wz-documents-page__toolbar-left">
            <h1 className="wz-documents-page__title">{t('wz:title')}</h1>
          </div>

          <div className="wz-documents-page__quick-actions">
            <Segmented<WarehouseDocumentListView>
              aria-label={t('wz:listView.label')}
              value={listView}
              onChange={handleListViewChange}
              options={[
                { value: 'active', label: t('wz:listView.active') },
                { value: 'cancelled', label: `${t('wz:listView.cancelled')} (${cancelledCount})` },
              ]}
            />
            <Input
              className="wz-documents-page__quick-search"
              placeholder={t('wz:filters.search')}
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
            <Button
              className={`wz-documents-page__filter-button ${filtersOpen ? 'wz-documents-page__filter-button--open' : ''}`}
              icon={<FilterOutlined />}
              onClick={() => setFiltersOpen((current) => !current)}
            >
              {t('wz:filtersHeading')}
              {activeFilterCount > 0 && (
                <span className="wz-documents-page__filter-count">{activeFilterCount}</span>
              )}
            </Button>
            <span className="wz-documents-page__count">
              {t('wz:totalDocuments', { total: filteredDocuments.length })}
            </span>
            {canEdit && (
              <Button
                className="wz-documents-page__create-button"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/app/wz/new')}
              >
                {t('wz:actions.newDocument')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {activeFilterCount > 0 && !filtersOpen && (
        <div className="wz-documents-page__filter-summary">
          <button
            type="button"
            className="wz-documents-page__filter-summary-button"
            onClick={() => setFiltersOpen(true)}
          >
            <FilterOutlined />
            <span>{t('wz:filtersHeading')} · {activeFilterCount}</span>
          </button>
          <Button type="text" size="small" icon={<ClearOutlined />} onClick={clearFilters}>
            {t('common:clearFilters')}
          </Button>
        </div>
      )}

      {filtersOpen && (
        <div className="wz-documents-filters">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={10}>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                placeholder={[t('wz:filters.dateFrom'), t('wz:filters.dateTo')]}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                block
                icon={<ClearOutlined />}
                onClick={clearFilters}
              >
                {t('common:clearFilters')}
              </Button>
            </Col>
          </Row>
        </div>
      )}

      <Card className="wz-documents-table-shell" bodyStyle={{ padding: 0 }}>
        <Table
          className="wz-documents-table"
          columns={columns}
          dataSource={filteredDocuments}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('common:totalItems', { count: total }),
          }}
        />
      </Card>

      <Modal
        title={t('wz:actions.documentPreview')}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            {t('common:close')}
          </Button>,
          ...(canDownloadWZPdf ? [
            <Button
              key="download"
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={() => previewDoc && handleDownloadPDF(previewDoc.id, previewDoc.wzNumber)}
              loading={downloading === previewDoc?.id}
            >
              {t('wz:actions.downloadPDF')}
            </Button>
          ] : [])
        ]}
        width={800}
      >
        {previewDoc && (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <p><strong>{t('wz:fields.wzNumber')}:</strong> {previewDoc.wzNumber}</p>
                <p><strong>{t('wz:fields.issueDate')}:</strong> {formatDate(previewDoc.issueDate)}</p>
                <p><strong>{t('wz:fields.recipient')}:</strong> {previewDoc.recipientName}</p>
              </Col>
              <Col span={12}>
                <p><strong>{t('wz:fields.orderNumber')}:</strong> {previewDoc.displayOrderNumber || previewDoc.orderNumber || '-'}</p>
              </Col>
            </Row>

            {previewDoc.attachments && previewDoc.attachments.length > 0 && (
              <div>
                <h4>{t('wz:fields.attachments')}:</h4>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {previewDoc.attachments.map((attachment, index: number) => (
                    <Card key={index} size="small">
                      <Space>
                        <FileOutlined />
                        <span>{attachment.fileName}</span>
                        <Button
                          type="link"
                          onClick={() => window.open(getAuthFileUrl(attachment.filePath), '_blank')}
                        >
                          {t('wz:actions.openFile')}
                        </Button>
                      </Space>
                    </Card>
                  ))}
                </Space>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WZDocuments;
